import { useState } from 'react'
import { Link } from 'react-router-dom'
import MobileNav from './MobileNav'
import ProductMenu from './ProductMenu'
import Logo from './Logo'
import { useUtmPassthrough } from '@/hooks/useUtmPassthrough'
import { appSignupLink } from '@/utils/signupUrl'
import { trackSignupCtaClick } from '@/utils/rum'

// Mobile-only nav. Desktop header is intentionally minimal: logo + log in + CTA.
// Same surface lives in the footer for desktop discovery.
const navLinks = [
  { label: 'Product', href: '/product' },
  { label: 'Pricing', href: '/pricing' },
  { label: 'Demo', href: '/demo' },
  { label: 'Case Studies', href: '/case-studies' },
]

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const utm = useUtmPassthrough()
  const signup = appSignupLink({ params: utm })
  const trackHeaderClick = () =>
    trackSignupCtaClick({ location: 'header', hasUtm: Object.keys(utm).length > 0 })

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen)
  }

  const closeMobileMenu = () => {
    setMobileMenuOpen(false)
  }

  return (
    <>
      <nav className="sticky top-0 z-[100] w-full bg-white/80 backdrop-blur-[20px] border-b border-slate-200 px-6">
        <div className="max-w-[1200px] mx-auto flex items-center justify-between h-20">
          {/* Logo */}
          <div onClick={closeMobileMenu}>
            <Logo variant="light" />
          </div>

          {/* Desktop Right: Product, log in + primary CTA */}
          <div className="hidden min-[900px]:flex items-center gap-6">
            <ProductMenu />
            <Link
              to="/pricing"
              className="text-sm text-slate-600 hover:text-slate-900 no-underline transition-colors"
            >
              Pricing
            </Link>
            <a
              href="https://app.wealthspace.co.uk"
              className="text-sm text-slate-500 hover:text-slate-900 no-underline transition-colors"
            >
              Log in
            </a>
            {signup.external ? (
              <a
                href={signup.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={trackHeaderClick}
                className="inline-flex items-center gap-2 bg-button-teal text-white px-5 py-2.5 rounded-md font-semibold text-sm transition-all duration-300 shadow-button hover:-translate-y-0.5 hover:shadow-button-hover no-underline"
              >
                Start Free Trial
              </a>
            ) : (
              <Link
                to={signup.href}
                onClick={trackHeaderClick}
                className="inline-flex items-center gap-2 bg-button-teal text-white px-5 py-2.5 rounded-md font-semibold text-sm transition-all duration-300 shadow-button hover:-translate-y-0.5 hover:shadow-button-hover no-underline"
              >
                Start Free Trial
              </Link>
            )}
          </div>

          {/* Hamburger Button - Mobile */}
          <button
            className="min-[900px]:hidden flex flex-col justify-center items-center w-11 h-11 bg-slate-100 border border-slate-200 rounded-lg cursor-pointer gap-[5px]"
            onClick={toggleMobileMenu}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
          >
            <span
              className={`block w-5 h-[2px] bg-slate-700 rounded-sm transition-all duration-300
                ${mobileMenuOpen ? 'rotate-45 translate-x-[5px] translate-y-[5px]' : ''}
              `}
            />
            <span
              className={`block w-5 h-[2px] bg-slate-700 rounded-sm transition-opacity duration-300
                ${mobileMenuOpen ? 'opacity-0' : ''}
              `}
            />
            <span
              className={`block w-5 h-[2px] bg-slate-700 rounded-sm transition-all duration-300
                ${mobileMenuOpen ? '-rotate-45 translate-x-[5px] -translate-y-[5px]' : ''}
              `}
            />
          </button>
        </div>
      </nav>

      {/* Mobile Menu: keeps full nav for tap-to-discover on small screens */}
      <MobileNav isOpen={mobileMenuOpen} onClose={closeMobileMenu} links={navLinks} />
    </>
  )
}
